import React, { useState } from 'react';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Switch from '@mui/material/Switch/Switch';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Alert from '@mui/material/Alert/Alert';
import { HackathonPayload, setIdeasOpen, setTeamsLocked } from '../../services/hackathon.service';
import { formatDate, ideaDeadlineEnd, resultsAnnounced, teamsLockedReason } from '../../utils/hackathonStatus';

/**
 * Organizer switches that don't wait for the dates or voting: close idea submissions now, and lock teams.
 * Both hold back participants only; organizers can still add ideas and move people between teams.
 */
export const EventControls = ({ hackathon, onChange }: { hackathon: HackathonPayload, onChange: () => void }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const run = (action: () => Promise<unknown>) => {
        setBusy(true);
        setError('');
        action().then(onChange, (err) => setError(err?.response?.data?.message || 'Could not change that')).finally(() => setBusy(false));
    };

    const deadline = ideaDeadlineEnd(hackathon.ideaSubmissionDeadline);
    const deadlinePassed = !!deadline && deadline.isBefore(new Date());
    const results = resultsAnnounced(hackathon);
    // locked for another reason too (voting, results, the event ended): say so, since the switch alone won't open them
    const otherLock = teamsLockedReason({ ...hackathon, teamsLocked: false });

    return (
        <Stack spacing={2}>
            <h2>Event controls</h2>
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Idea submissions</Typography>
                    {
                        hackathon.ideasClosedAt
                            ? <Button size="small" variant="outlined" disabled={busy || results} onClick={() => run(() => setIdeasOpen(hackathon._id, true))}>Reopen submissions</Button>
                            : <Button size="small" variant="outlined" color="warning" disabled={busy || results || deadlinePassed} onClick={() => run(() => setIdeasOpen(hackathon._id, false))}>Close submissions now</Button>
                    }
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    {results
                        ? 'Closed: the results have been announced.'
                        : hackathon.ideasClosedAt
                            ? <>Closed by the organizers on {formatDate(hackathon.ideasClosedAt, 'ddd, MMM D [at] h:mm A')}{deadlinePassed ? ', and the deadline has passed too' : ''}. Participants can't pitch; you still can, also on someone's behalf.</>
                            : deadlinePassed
                                ? <>Closed: the deadline ({formatDate(hackathon.ideaSubmissionDeadline, 'ddd, MMM D')}) has passed. Change it in Edit hackathon to reopen. You can still add ideas.</>
                                : <>Open until the end of {formatDate(hackathon.ideaSubmissionDeadline, 'ddd, MMM D') || 'the event'}. Close them early if you have enough ideas.</>}
                </Typography>
            </Stack>
            <Stack spacing={0.5}>
                <FormControlLabel
                    control={<Switch checked={!!hackathon.teamsLocked} disabled={busy} onChange={(e) => run(() => setTeamsLocked(hackathon._id, e.target.checked))} />}
                    label={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Lock teams</Typography>} />
                <Typography variant="body2" color="text.secondary">
                    {hackathon.teamsLocked
                        ? <>Teams are locked: participants can't join, leave or switch teams. You can still move people in the table below.</>
                        : <>Participants can join, leave and switch teams. Lock them when teams are settled; you can still move people yourself.</>}
                    {otherLock && <> Also, right now: {otherLock.charAt(0).toLowerCase() + otherLock.slice(1)}.</>}
                </Typography>
            </Stack>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        </Stack>
    );
}
